import { useMemo } from 'react';

import { Box, Checkbox, Paper, Stack, Typography } from '@mui/material';
import {
  DataGridPremium,
  type GridColDef,
  type GridSortModel,
} from '@mui/x-data-grid-premium';

import { Order_By } from '@graphql/__generated__/graphql';
import { processMultipleOrderByFields } from '@modules/_shared/components/data-grid/RaDataGrid';
import {
  formatDistance,
  toHumanReadableDate,
} from '@modules/_shared/utils/formatting';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import {
  type TransactionPresentation,
  computeTransactionPresentation,
  usePresentationContext,
} from '@modules/reports/cma-reports/step-transaction/transactionPresentation';
import type {
  MergedTransactionRow,
  TransactionSource,
} from '@modules/reports/cma-reports/step-transaction/types';
import { isToggleSelectionKey } from '@modules/reports/cma-reports/step-transaction/utils/keyboard';
import { encodeSelectionKey } from '@modules/reports/cma-reports/step-transaction/utils/selectionKey';
import { useLocale } from '@shared/hooks/locale';

type RowWithPresentation = MergedTransactionRow & {
  _presentation: TransactionPresentation;
};

type PresentationCellProps = {
  row: RowWithPresentation;
  field: keyof TransactionPresentation;
};

const PresentationCell: React.FC<PresentationCellProps> = ({ row, field }) => {
  return (
    <Typography variant="body2">{row._presentation[field] ?? '-'}</Typography>
  );
};

type LocationCellProps = { row: RowWithPresentation };

const LocationCell = ({ row }: LocationCellProps) => {
  const { locality, postcode, streetLabel } = row._presentation;
  const locationText =
    locality && postcode ? `${locality} (${postcode})` : locality ?? '-';

  return (
    <Stack sx={{ justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2">{locationText}</Typography>
      {streetLabel && (
        <Typography variant="caption" color="text.secondary">
          {streetLabel}
        </Typography>
      )}
    </Stack>
  );
};

type PriceCellProps = { row: RowWithPresentation };

const PriceCell = ({ row }: PriceCellProps) => {
  const { priceLabel, pricePerSqmLabel } = row._presentation;
  return (
    <Stack sx={{ justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {priceLabel ?? '-'}
      </Typography>
      {pricePerSqmLabel && (
        <Typography variant="caption" color="text.secondary">
          {pricePerSqmLabel}
        </Typography>
      )}
    </Stack>
  );
};

type TransactionListViewProps = {
  transactions: MergedTransactionRow[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string, source: TransactionSource) => void;
  loading: boolean;
  totalCount: number;
  queryParamsScope: string;
};

export const TransactionListView = ({
  transactions,
  selectedIds,
  onToggleSelect,
  loading,
  totalCount,
  queryParamsScope,
}: TransactionListViewProps) => {
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] =
    useScopedSearchParams(queryParamsScope);

  const presentationCtx = usePresentationContext();
  const { isTenantSpanish } = presentationCtx;

  const { limit, offset } = getPaginationVariables(searchParams);
  const page = Math.floor(offset / limit);
  const pageSize = limit;

  const enrichedRows = useMemo<RowWithPresentation[]>(
    () =>
      transactions.map(row => ({
        ...row,
        _presentation: computeTransactionPresentation(row, presentationCtx),
      })),
    [transactions, presentationCtx],
  );

  const rowsById = useMemo(
    () => new Map(enrichedRows.map(row => [row.id, row])),
    [enrichedRows],
  );

  const columns = useMemo<GridColDef<RowWithPresentation>[]>(
    () => [
      {
        field: 'select',
        headerName: '',
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Checkbox
            checked={selectedIds.has(
              encodeSelectionKey(row.source, row.transaction_id),
            )}
            onChange={() => onToggleSelect(row.transaction_id, row.source)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                onToggleSelect(row.transaction_id, row.source);
              }
            }}
            size="small"
          />
        ),
      },
      {
        field: 'transaction.property_type_name',
        headerName: t('Property type'),
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_, row) => row._presentation.propertyTypeLabel ?? '',
        renderCell: ({ row }) => (
          <PresentationCell row={row} field="propertyTypeLabel" />
        ),
      },
      {
        field: 'transaction.locality',
        headerName: t('Location'),
        flex: 1.2,
        minWidth: 180,
        valueGetter: (_, row) => row.transaction?.locality || '',
        renderCell: ({ row }) => <LocationCell row={row} />,
      },
      {
        field: 'distance_in_meters',
        headerName: t('Distance'),
        width: 110,
        valueGetter: (_, row) => row._presentation.distance ?? 0,
        renderCell: ({ row }) => {
          const { distance } = row._presentation;
          return (
            <Typography variant="body2">
              {distance != null ? formatDistance(distance) : '-'}
            </Typography>
          );
        },
      },
      {
        field: isTenantSpanish
          ? 'transaction.number_of_bedrooms'
          : 'transaction.number_of_rooms',
        headerName: t('Rooms'),
        width: 90,
        valueGetter: (_, row) => row._presentation.rooms,
        renderCell: ({ row }) => <PresentationCell row={row} field="rooms" />,
      },
      {
        field: isTenantSpanish
          ? 'transaction.built_surface'
          : 'transaction.living_surface',
        headerName: isTenantSpanish
          ? t('Built Surface (m²)')
          : t('Living Surface (m²)'),
        width: 130,
        valueGetter: (_, row) => row._presentation.surface,
        renderCell: ({ row }) => (
          <PresentationCell row={row} field="surfaceLabel" />
        ),
      },
      {
        field: 'transaction.land_surface',
        headerName: t('Land Surface (m²)'),
        width: 130,
        valueGetter: (_, row) => row.transaction?.land_surface ?? 0,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {row.transaction?.land_surface
              ? `${row.transaction?.land_surface} m²`
              : '-'}
          </Typography>
        ),
      },
      {
        field: 'transaction.signature_date',
        headerName: t('Sold Date'),
        width: 140,
        valueGetter: (_, row) => row.transaction?.signature_date ?? '',
        renderCell: ({ row }) => {
          const date = row.transaction?.signature_date;
          return (
            <Typography variant="body2">
              {date
                ? toHumanReadableDate(locale, date, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })
                : '-'}
            </Typography>
          );
        },
      },
      {
        field: 'transaction.purchase_price',
        headerName: t('Price'),
        flex: 1,
        minWidth: 150,
        valueGetter: (_, row) => row.transaction?.purchase_price ?? null,
        renderCell: ({ row }) => <PriceCell row={row} />,
      },
    ],
    [t, isTenantSpanish, selectedIds, onToggleSelect, locale],
  );

  const handlePageChange = (newPage: number) => {
    const newOffset = newPage * pageSize;
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set('offset', newOffset.toString());
    setSearchParams(updatedSearchParams);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set('limit', newPageSize.toString());
    updatedSearchParams.set('offset', '0');
    setSearchParams(updatedSearchParams);
  };

  if (transactions.length === 0 && !loading) {
    return (
      <Stack
        sx={{
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            p: 2,
            textAlign: 'center',
          }}
        >
          {t('No transactions found')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
      }}
      component={Paper}
    >
      <DataGridPremium
        loading={loading}
        columns={columns}
        rows={enrichedRows}
        rowCount={totalCount}
        pagination
        disableColumnMenu
        pageSizeOptions={[25, 50, 100]}
        onPaginationModelChange={model => {
          if (model.page !== page) {
            handlePageChange(model.page);
          }
          if (model.pageSize !== pageSize) {
            handlePageSizeChange(model.pageSize);
          }
        }}
        paginationMode="server"
        sortingMode="server"
        onSortModelChange={(sortModel: GridSortModel) => {
          const order_by = sortModel.flatMap(({ field, sort }) =>
            processMultipleOrderByFields(field, sort),
          );

          const updatedSearchParams = new URLSearchParams(searchParams);
          updatedSearchParams.set('order_by', JSON.stringify(order_by));
          updatedSearchParams.set('offset', '0');
          setSearchParams(updatedSearchParams);
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          sorting: {
            sortModel: [{ field: 'distance_in_meters', sort: Order_By.Asc }],
          },
        }}
        rowHeight={60}
        disableRowSelectionOnClick
        onRowClick={({ row }) => onToggleSelect(row.transaction_id, row.source)}
        slotProps={{
          row: {
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              const target = e.target as HTMLElement;
              const rowElement = target.closest('.MuiDataGrid-row');
              const rowId = rowElement?.getAttribute('data-id');
              if (rowId && isToggleSelectionKey(e)) {
                e.preventDefault();
                const row = rowsById.get(rowId);
                if (row) {
                  onToggleSelect(row.transaction_id, row.source);
                }
              }
            },
          },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&:focus': {
              outline: theme =>
                `2px solid ${(theme.vars ?? theme).palette.primary.main}`,
              outlineOffset: -2,
            },
            '&:focus:not(:focus-visible)': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
};
